import React from 'react'
import PropTypes from 'prop-types'
import { useAbundanceEngineBranding, useDeleteBrand, useGetDistinctBrandings } from '../../../../../api/abundanceEngine'
import EmptySection from '../../../../atoms/EmptySection'
import SydButton from '../../../../commonDesign/Button'
import OperationalTable from '../../../../organisms/OperationalTable'
import Loading from '../../../../molecules/Loading'
import { useCheckPolicy } from '../../../../../hooks'
import { PERMISSIONS } from '../../../../../policies/admin'
import { useFeatureFlag } from '../../../../../redux/slices/appConfig'
import { FEATURE_FLAG } from '../../../../../constants'
import ConfirmationModal from '../../../../molecules/ConfirmationModal'
import BrandModal from './BrandModal'
import BrandEditModal from './brandEditModal'

const Brands = () => {
  const { active: canViewBrands } = useFeatureFlag(FEATURE_FLAG.MULTI_BRAND_FIRM)
  const canAddBrands = useCheckPolicy(PERMISSIONS.brandConfiguration)
  const { data: brandsInUse, isLoading: isLoadingBrands } = useGetDistinctBrandings()
  const [deletedId, setDeletedId] = React.useState(null)
  const { mutateAsync, isLoading: deleteLoading } = useDeleteBrand()
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false)

  const defaultColumnConfig = {
    columns: [
      { Header: 'Brand Name', accessor: 'brandKey', type: 'string' },
      { Header: 'Wealth Mission', accessor: 'tagLine', type: 'string' },
      { Header: 'Firm Logo', accessor: 'logoUrl', type: 'string' },
      { Header: 'Firm Icon', accessor: 'data.icons.favicon', type: 'string' },
      {
        Header: 'Actions',
        accessor: 'actions',
        Cell: ({ row }) => ActionsCell({
          canEditContent: canAddBrands,
          canDelete: canAddBrands && row.original.brandKey !== 'default' && !brandsInUse.includes(row.original.brandKey),
          editFunction: () => {
            setEditModalOpen(true)
            setBrandSchema(row.original)
          },
          deleteFunction: () => {
            setDeletedId(row.original.brandKey)
            setDeleteModalOpen(true)
          }
        })
      }
    ],
    defaultSort: [{ id: 'ordinal', desc: false }]
  }
  const { data: brands, isLoading, error } = useAbundanceEngineBranding()
  const [brandSchema, setBrandSchema] = React.useState(null)
  const [editModalOpen, setEditModalOpen] = React.useState(false)
  const [addModalOpen, setAddModalOpen] = React.useState(false)
  const setOpenAddBrand = (openModal) => {
    openModal ? setAddModalOpen(true) : setEditModalOpen(true)
  }
  if (!canViewBrands) {
    return (
      <EmptySection
        title='Access Denied'
        description='You do not have sufficient permissions to access this resource'
      />
    )
  }

  if (isLoading || isLoadingBrands) {
    return <Loading />
  }

  if (error) {
    return <div>Error loading brands: {error.message}</div>
  }

  return (
    <>
      <ConfirmationModal
        open={deleteModalOpen || deleteLoading}
        onCancel={() => setDeleteModalOpen(false)}
        onConfirm={async () => {
          const result = await mutateAsync(deletedId)
          if (result.success) {
            setDeletedId(null)
            setDeleteModalOpen(false)
          } else {
            console.error(result)
          }
        }}
        isLoading={deleteLoading}
        confirmationType='delete'
        confirmationText='Are you sure you want to delete?'
        buttonLabelCancel='Cancel'
        buttonLabelConfirm='Delete'
      />

      {addModalOpen && (
        <BrandModal
          open={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          brandData={brandSchema}
        />
      )}
      {editModalOpen && (
        <BrandEditModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          brandSchema={brandSchema}
        />
      )}

      <div>
        <OperationalTable.Wrapper>
          {canAddBrands && (
            <div className='flex flex-row justify-end mb-4'>
              <SydButton size='md' variant='primary' icon='add' onClick={() => setOpenAddBrand(true)}>Add Branding</SydButton>
            </div>
          )}
          <OperationalTable
            variant='v2'
            mode='auto'
            itemName='Branding'
            columns={defaultColumnConfig.columns}
            defaultSort={defaultColumnConfig.defaultSort}
            data={brands}
            total={brands.length}
          />
        </OperationalTable.Wrapper>
      </div>
    </>
  )
}

export default Brands

export const ActionsCell = ({ canDelete, deleteFunction, editFunction, canEditContent }) => {
  return (
    <div className='flex flex-row items-center'>
      <div className='flex flex-row gap-2 pr-4'>
        <SydButton size='sm' variant='outline' icon='edit' onClick={() => editFunction()} disabled={!canEditContent}>Edit</SydButton>
        <SydButton size='sm' variant='outline' priority='warning' icon='delete' onClick={deleteFunction} disabled={!canDelete || !canEditContent}>Delete</SydButton>
      </div>
    </div>
  )
}

ActionsCell.propTypes = {
  canDelete: PropTypes.bool,
  editFunction: PropTypes.func,
  deleteFunction: PropTypes.func,
  canEditContent: PropTypes.bool
}
